// The patient's waitlist requests, on the Appointments page.
//
// Client ticket 328. Until this existed the portal could put somebody on a list
// and then offer no way to see it, change it or leave — they could join from
// the booking site and were then stuck.
//
// ## It is a tab, chosen deliberately
//
// Jane stacks its "Wait List Requests" under the appointments on one screen,
// and the tabs beside this one are filters over appointments rather than over
// objects of different kinds. A tab was chosen anyway, because the thing a
// patient looks for is "where do I see my waitlist" and a tab is where they
// look. Discoverability beat taxonomy.
//
// What that costs, and is paid for here: the tab needs a COUNT, so the list is
// loaded by the page through `useMyWaitlistRequests` and handed down. The
// alternative — this component fetching for itself while the tab counts
// separately — is two answers to "how many am I waiting on" on the same screen.
//
// ## Nothing here recomputes a rule the engine owns
//
// The queue position after an edit comes from the server's
// `keptQueuePosition`, and the order of the list is the order the server sent
// (`createdAt` IS the queue position). A screen that sorted differently from
// the engine would teach the patient the wrong mental model about who is next.

import {
  Box,
  Button,
  Flex,
  Grid,
  Icon,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {useMemo, useState} from "react";
import {FiClock} from "react-icons/fi";
import {askToJoinWaitlist, leaveMyWaitlistRequest, replaceMyWaitlistRequest} from "./api";
import WaitlistRequestCard from "./WaitlistRequestCard";
import WaitlistRequestDrawer from "./WaitlistRequestDrawer";
import LeaveWaitlistDialog from "./LeaveWaitlistDialog";

export default function WaitlistRequests({
  dashState,
  entries,
  allowEdits,
  askAt = [],
  loading,
  failed,
  reload,
}) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  // The drawer: a new request (`entry` null) or the one being changed.
  const [drawer, setDrawer] = useState({open: false, entry: null});
  const [leaving, setLeaving] = useState(null);
  const [leaveProblem, setLeaveProblem] = useState("");
  const leaveDialog = useDisclosure();

  // Each request comes named by the server (serviceName, clinicianName, locationName), whichever site it is at: the
  // app's own directory holds only the patient's first site. It still gives a clinician's photo where it has one.
  const cliniciansById = useMemo(() => {
    const byId = {};
    (dashState.doctors || []).forEach((doctor) => {
      byId[doctor.did] = doctor;
    });
    return byId;
  }, [dashState.doctors]);

  const closeDrawer = () => setDrawer((current) => ({...current, open: false}));

  // The drawer says a refusal in its own summary, so these only confirm what the patient did (a toast never says an error).
  async function handleSubmit(request) {
    if (drawer.entry) {
      const result = await replaceMyWaitlistRequest(dashState, drawer.entry.entryId, request);
      // Told, not inferred: what costs the queue position is the engine's rule, read back from its answer.
      toast({
        title: "Your request was changed",
        description: result.keptQueuePosition
          ? "You kept your place in the queue."
          : "This is a new request, so it starts at the back of the queue.",
        status: "success",
        duration: 6000,
        isClosable: true,
      });
    } else {
      await askToJoinWaitlist(dashState, request);
      toast({
        title: "You are on the waitlist",
        description: "We will let you know when a time opens up.",
        status: "success",
        duration: 6000,
        isClosable: true,
      });
    }
    closeDrawer();
    await reload();
  }

  async function handleLeave() {
    setSaving(true);
    setLeaveProblem("");
    try {
      await leaveMyWaitlistRequest(dashState, leaving.entryId);
      toast({
        title: "You have been taken off the list",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      leaveDialog.onClose();
      setLeaving(null);
      await reload();
    } catch (error) {
      // Said in the dialog, next to the act, and kept until they try again or close it.
      setLeaveProblem(error?.response?.data?.error || "You are still on the list: nothing was changed. Check your connection, then try again.");
    } finally {
      setSaving(false);
    }
  }

  // Where a new request can be asked for: the action, or where to ask instead.
  const askAction =
    askAt.length > 0 ? (
      <button
        type="button"
        onClick={() => setDrawer({open: true, entry: null})}
        className="min-h-[var(--blh-control-height-touch)] rounded-blh-md bg-blh-brand px-blh-400 text-blh-body font-semibold text-blh-on-brand hover:bg-blh-brand-ink focus-visible:outline focus-visible:outline-[length:var(--blh-focus-ring-width)] focus-visible:outline-offset-[var(--blh-focus-ring-offset)] focus-visible:outline-blh-focus"
      >
        Join the waitlist
      </button>
    ) : (
      <p className="m-0 text-blh-body text-blh-ink-2">To join a waitlist, call the clinic.</p>
    );

  // NOTHING IS HIDDEN. As a stacked section this returned null when there was
  // nothing to show, which was right there and wrong here: somebody who clicked
  // a tab called Waitlist and got a blank panel learns nothing, and a failed
  // read looked exactly like an empty list.

  return (
    <Box>
      {loading ? (
        <Flex justify="center" py={8}>
          <Spinner size="sm" color="#6b7280" />
        </Flex>
      ) : failed ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          py={16}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="8px"
        >
          <Icon as={FiClock} fontSize="32px" color="gray.300" mb={3} />
          <Text fontSize="14px" color="#6b7280" fontWeight="500">
            Could not load your waitlist requests
          </Text>
          <Text fontSize="12px" color="gray.400" mt={1}>
            This does not mean you are on no list. Refresh, or call the clinic.
          </Text>
          <Button size="xs" variant="outline" mt={4} onClick={reload}>
            Try again
          </Button>
        </Flex>
      ) : entries.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          py={16}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="8px"
        >
          <Icon as={FiClock} fontSize="32px" color="gray.300" mb={3} />
          <Text fontSize="14px" color="#6b7280" fontWeight="500">
            No waitlist requests
          </Text>
          <Text fontSize="12px" color="var(--blh-ink-2)" mt={1} mb={4}>
            We let you know when an earlier time opens up.
          </Text>
          {askAction}
        </Flex>
      ) : (
        <>
        <Flex justify="flex-end" mb={3}>
          {askAction}
        </Flex>
        <Grid
          templateColumns={{base: "1fr", lg: "1fr 1fr"}}
          gap={3}
        >
          {entries.map((entry) => (
            <WaitlistRequestCard
              key={entry.entryId}
              entry={entry}
              serviceName={entry.serviceName || String(entry.serviceId)}
              clinicianName={
                entry.clinicianId
                  ? entry.clinicianName || "Your clinician"
                  : "Anyone available"
              }
              clinician={cliniciansById[entry.clinicianId]}
              locationName={entry.locationName || ""}
              // A request tied to a booked visit changes with that visit, at the clinic (the server refuses it here).
              editable={allowEdits[entry.locationId] !== false && entry.kind !== "anchored"}
              onEdit={() => setDrawer({open: true, entry})}
              onLeave={() => {
                setLeaving(entry);
                leaveDialog.onOpen();
              }}
            />
          ))}
        </Grid>
        </>
      )}

      <WaitlistRequestDrawer
        isOpen={drawer.open}
        onClose={closeDrawer}
        entry={drawer.entry}
        askAt={askAt}
        locationName={drawer.entry?.locationName || ""}
        serviceName={drawer.entry?.serviceName || ""}
        clinicianName={drawer.entry?.clinicianName || ""}
        onSubmit={handleSubmit}
      />

      <LeaveWaitlistDialog
        isOpen={leaveDialog.isOpen}
        onClose={() => {
          leaveDialog.onClose();
          setLeaving(null);
          setLeaveProblem("");
        }}
        problem={leaveProblem}
        entry={leaving}
        serviceName={leaving?.serviceName || ""}
        onConfirm={handleLeave}
        isLoading={saving}
      />
    </Box>
  );
}
